import { QueryFactory, UserDto } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  pagingQueryParams,
  sortingQueryParams,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import { PageSize } from '@lmt/unicorn-shared/app/components/common/table/types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { permissionDisplayFilter } from '@lmt/unicorn-shared/app/components/roles/list/components/RolesTable';
import { UsersTable } from '@lmt/unicorn-shared/app/components/users/list/components/UsersTable';
import { IUserInfo } from '@lmt/unicorn-shared/app/components/users/list/types';
import { useIsSuperUser } from '@lmt/unicorn-shared/app/components/users/superuser/utils/useIsSuperUser';
import { useRoles } from '@lmt/unicorn-shared/app/features/roles/selectors';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useUsers } from '@lmt/unicorn-shared/app/features/users/selectors';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { localizePermission } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';

import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { snakeCase } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BooleanParam,
  StringParam,
  withDefault,
} from 'react-router-url-params';
import { useDebouncedCallback } from 'use-debounce';
import styles from './UserList.module.scss';
import { useEventCallback } from '@mui/material';

export const tenantAdministratorRoleName = 'TenantAdministrator';
const useGetUsers = (props: {
  search: string | null | undefined;
  isOnlyTenantAdministrators: boolean | null | undefined;
  sortBy: string | null | undefined;
  desc: boolean | null | undefined;
}): IUserInfo[] => {
  const i18next = useTranslation();
  const users = useUsers();
  const roles = useRoles();

  const getUserRoles = useCallback(
    (roleIds: number[]): string[] =>
      roles
        .filter((role) => roleIds.includes(role.id))
        .map((userRole) => i18next.t(userRole.name as any)),
    [roles],
  );
  const getPermissions = useCallback(
    (roleIds: number[]): string[] => {
      const permissionsArrays = roles
        .filter((role) => roleIds.includes(role.id) && role.permissions)
        .map((userRole) => userRole.permissions!);
      const permissions = ([] as string[])
        .concat(...permissionsArrays)
        .filter((permission) => !permissionDisplayFilter.includes(permission))
        .map((permission) => localizePermission(permission))
        .sort((a, b) => a.localeCompare(b));
      return [...new Set(permissions)];
    },
    [roles],
  );

  const subtenants = useSubtenants();
  const getUserSubtenantNames = useCallback(
    (subtenantIds: number[]): string[] =>
      subtenants
        .filter((subtenant) => subtenantIds.includes(subtenant.id))
        .map((userSubtenant) => userSubtenant.name),
    [subtenants],
  );

  const tenantAdministratorRoleId = useMemo(
    () => roles.find((x) => x.name === tenantAdministratorRoleName)?.id,
    [roles],
  );

  const usersFilteredByRole = useMemo(() => {
    if (!props.isOnlyTenantAdministrators || !tenantAdministratorRoleId) {
      return users;
    }
    return users.filter((x) => x.roles.includes(tenantAdministratorRoleId));
  }, [users, tenantAdministratorRoleId, props.isOnlyTenantAdministrators]);

  const filteredUsers = useMemo(() => {
    return usersFilteredByRole
      .map((user) => {
        const roles = getUserRoles(user.roles);
        return {
          ...user,
          roles: roles,
          permissions: getPermissions(user.roles),
          subtenants: getUserSubtenantNames(user.subtenants),
        };
      })
      .filter((user) => {
        if (props.search) {
          const searchableString = (
            user.username +
            ' ' +
            user.firstName +
            ' ' +
            user.lastName +
            ' ' +
            user.roles
              .map((role) =>
                i18next.t(`roles.list.default_roles.${snakeCase(role)}`, role),
              )
              .join(' ') +
            ' ' +
            user.subtenants.join(' ')
          ).toLowerCase();
          return searchableString.includes(props.search.toLowerCase());
        }
        return true;
      });
  }, [usersFilteredByRole, subtenants, roles, props.search]);

  return useMemo(() => {
    const field = props.sortBy;
    if (field) {
      const value = props.desc ? -1 : 1;
      return [...filteredUsers].sort((u1: any, u2: any) => {
        if (typeof u1[field] === 'string' || typeof u2[field] === 'string') {
          return u1[field].toString().toLowerCase() >
            u2[field].toString().toLowerCase()
            ? value
            : -value;
        }

        return u1[field] > u2[field] ? value : -value;
      });
    }
    return filteredUsers;
  }, [filteredUsers, props.sortBy, props.desc]);
};

export const UserList: FC = React.memo(function UserList() {
  const i18n = useScopedTranslation('users.list');
  const dispatch = useUniDispatch();
  const modals = useModal();
  const isSuperUser = useIsSuperUser();

  const [queryParams, setQueryParams] = useQueryParamsInTab({
    search: StringParam,
    isOnlyTenantAdministrators: withDefault(BooleanParam, isSuperUser), // superuser manages only tenant administrators
    ...pagingQueryParams(PageSize.Small),
    ...sortingQueryParams(nameof<UserDto>('lastName'), false),
  });
  const data = useGetUsers(queryParams);

  const forms = useGlobalForms();
  const openCreate = useEventCallback(() => forms.openUserCreating());

  const openEdit = useEventCallback((userId: string) =>
    forms.openUserEditing(userId),
  );

  const lockUser = useEventCallback(async (userId: string) => {
    const isConfirmed = await modals.showConfirm({
      text: i18n.t('confirm_message.lock_user'),
    });

    if (isConfirmed) {
      try {
        await QueryFactory.UserClient.lockUser(userId);
      } catch (error) {
        await modals.showError({
          text: i18n.t('errors.lock_user') + '\n' + convertToErrorString(error),
        });
      }
    }
  });

  const unlockUser = useEventCallback(async (userId: string) => {
    try {
      await QueryFactory.UserClient.unlockUser(userId);
    } catch (error) {
      await modals.showError({
        text: i18n.t('errors.unlock_user') + '\n' + convertToErrorString(error),
      });
    }
  });

  const deleteUser = useEventCallback(async (userId: string) => {
    const isConfirmed = await modals.showConfirm({
      text: i18n.t('confirm_message.delete_user'),
    });
    if (!isConfirmed) return;
    try {
      await QueryFactory.UserClient.removeUser(userId);
    } catch (error) {
      await modals.showError({
        text: i18n.t('errors.delete_user') + '\n' + convertToErrorString(error),
      });
    }
  });
  const changePassword = useEventCallback(async (userId: string) => {
    forms.setUserPassword(userId);
  });

  const onSearchUpdated = useDebouncedCallback((value: string) => {
    setQueryParams({
      search: value,
    });
  }, 300);

  useInsertKeyHandler(openCreate);

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle title={i18n.t('title')} count={data.length} />
        <div className={tableStyles.searchField}>
          <SearchInput
            placeholder={i18n.t('search_placeholder')}
            bordered={true}
            onChange={(e) => onSearchUpdated(e.target.value)}
            onSearchIconClick={onSearchUpdated}
            onClear={onSearchUpdated}
          />
        </div>
        {!isSuperUser && (
          <Button
            title={i18n.t('add_button')}
            color={ButtonColor.Primary}
            className={styles.addButton}
            onClick={openCreate}
            iconComponent={<AddIcon />}
            testId={'plus-button'}
          />
        )}
      </div>

      <div className={tableStyles.tableContainer}>
        <Loading
          loading={false}
          title={i18n.t('loading')}
          className={styles.loading}
        >
          <UsersTable
            data={data}
            noResults={false}
            changeSorting={setQueryParams}
            onUnlock={unlockUser}
            onLock={lockUser}
            onDelete={deleteUser}
            onSetPassword={changePassword}
            onEdit={openEdit}
            isSuperUser={isSuperUser}
          />
        </Loading>
      </div>
      {/*<div className={styles.pagination}>*/}
      {/*  <TablePagination*/}
      {/*    pagination={props.pagination}*/}
      {/*    pageLimitTitle={i18n.t('Table_Pagination_PageLimit')}*/}
      {/*    changePagination={setPagination}*/}
      {/*    totalCount={messages?.totalCount ?? 0}*/}
      {/*  />*/}
      {/*</div>*/}
    </div>
  );
});
